import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import { useProgress, OWNER_KEY } from "@/modules/premiere-video/store";
import { Btn } from "@/components/kit";

const MeCtx = createContext(null);
export const useMe = () => useContext(MeCtx);

export function FullLoader({ label = "Chargement de ton espace…" }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ivory text-soft" data-testid="full-loader">
      <Loader2 className="h-6 w-6 animate-spin text-bx" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

function CloudSync({ userId, children }) {
  const { state, replaceAll, reset, setSync } = useProgress();
  const api = useApi();
  const [ready, setReady] = useState(false);
  const skipFirst = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const owner = localStorage.getItem(OWNER_KEY);
        const res = await api("GET", "/progress");
        if (!alive) return;
        if (res.data) {
          replaceAll(res.data);
          skipFirst.current = true;
        } else if (owner && owner !== userId) reset();
        localStorage.setItem(OWNER_KEY, userId);
        setSync("synced");
      } catch {
        setSync("error");
      }
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [userId, api, replaceAll, reset, setSync]);

  useEffect(() => {
    if (!ready) return;
    if (skipFirst.current) { skipFirst.current = false; return; }
    setSync("saving");
    const t = setTimeout(() => {
      api("PUT", "/progress", { data: state }).then(() => setSync("synced")).catch(() => setSync("error"));
    }, 800);
    return () => clearTimeout(t);
  }, [state, ready, api, setSync]);

  if (!ready) return <FullLoader label="Récupération de ton parcours…" />;
  return children;
}

export function useMeQuery() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const api = useApi();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return setMe(null);
    setError(false);
    api("GET", "/me").then(setMe).catch(() => setError(true));
  }, [isLoaded, isSignedIn, userId, api]);
  return { isLoaded, isSignedIn, userId, me, error };
}

export default function AccessGate() {
  const { isLoaded, isSignedIn, userId, me, error } = useMeQuery();
  if (!isLoaded) return <FullLoader />;
  if (!isSignedIn) return <Navigate to="/acces" replace />;
  if (error)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ivory px-6 text-center">
        <p className="text-ink">Impossible de vérifier ton accès pour le moment.</p>
        <Btn onClick={() => window.location.reload()} data-testid="gate-retry-button">Réessayer</Btn>
      </div>
    );
  if (!me) return <FullLoader />;
  if (!me.has_access) return <Navigate to="/acces" replace />;
  return (
    <MeCtx.Provider value={me}>
      <CloudSync userId={userId}>
        <Outlet />
      </CloudSync>
    </MeCtx.Provider>
  );
}

export function SignOutWatcher() {
  const { isLoaded, isSignedIn } = useAuth();
  const { reset } = useProgress();
  const prev = useRef(null);
  useEffect(() => {
    if (!isLoaded) return;
    if (prev.current === true && !isSignedIn) {
      reset();
      localStorage.removeItem(OWNER_KEY);
    }
    prev.current = isSignedIn;
  }, [isLoaded, isSignedIn, reset]);
  return null;
}
